import { Node, Path } from 'slate';

import * as serializer from 'basic-editor-value-serializer';

import HelptapEditor from '../../../helpers';

import { findCells, findEdges, findParentFlow, readFlow, readRectangles } from './layout-flow';

const types = serializer.types;

// Pointer over the layout itself, in a gap or its padding along the axis.
function findDropIntoLayout(editor, element, point, targetPath) {

  if (Node.get(editor, targetPath).type !== types.LAYOUT) {
    return null;
  }

  const found = findCells(element);

  if (found.container === null || found.cells.length === 0) {
    return null;
  }

  const flow = readFlow(found.container);
  const index = HelptapEditor.layout.findLayoutIndex(readRectangles(found.cells), point, flow);

  if (index === -1) {
    return null;
  }

  const edges = findEdges(flow);
  const plan = { kind: 'move', parentPath: targetPath, index, targetPath };

  if (index < found.cells.length) {
    return { plan, element: found.cells[index].firstElementChild, edge: edges.leading, makesLayout: false };
  }

  return { plan, element: found.cells[found.cells.length - 1].firstElementChild, edge: edges.trailing, makesLayout: false };

}

function involvesLayout(editor, sourcePaths, targetPath) {

  if (HelptapEditor.layout.isInsideLayout(editor, targetPath) === true || Node.get(editor, targetPath).type === types.LAYOUT) {
    return true;
  }

  for (let index = 0; index < sourcePaths.length; index++) {
    if (HelptapEditor.layout.isInsideLayout(editor, sourcePaths[index]) === true) {
      return true;
    }
  }

  return false;

}

function findDropAroundTarget(editor, element, point, sourcePaths, targetPath) {

  const flow = findParentFlow(element);
  const wrapPlan = { kind: 'wrap', targetPath, direction: 'row', side: 'start' };

  if (flow.axis === 'row') {
    wrapPlan.direction = 'column';
  }

  flow.canWrap = HelptapEditor.layout.canApplyDrop(editor, sourcePaths, wrapPlan);

  const found = HelptapEditor.layout.findTargetZone(element.getBoundingClientRect(), point, flow);

  if (found.zone !== 'before' && found.zone !== 'after') {
    wrapPlan.side = found.zone.split('-')[1];
    return { plan: wrapPlan, element, edge: found.edge, makesLayout: true };
  }

  if (involvesLayout(editor, sourcePaths, targetPath) === false) {
    return null;
  }

  let index = targetPath[targetPath.length - 1];

  if (found.zone === 'after') {
    index++;
  }

  return { plan: { kind: 'move', parentPath: Path.parent(targetPath), index, targetPath }, element, edge: found.edge, makesLayout: false };

}

function findDestination(plan) {

  if (plan.kind === 'wrap') {
    return plan.targetPath;
  }

  return plan.parentPath.concat(plan.index);

}

// The drop at a target when a layout is involved: { plan, accepted, element, edge, makesLayout }.
// Returns null when no layout is involved, so today's rules apply.
export function findLayoutDrop(editor, element, event, sourcePaths, targetPath, selection) {

  const point = { x: event.clientX, y: event.clientY };
  let drop = findDropIntoLayout(editor, element, point, targetPath);

  if (drop === null) {
    drop = findDropAroundTarget(editor, element, point, sourcePaths, targetPath);
  }

  if (drop === null) {
    return null;
  }

  drop.accepted = HelptapEditor.layout.canApplyDrop(editor, sourcePaths, drop.plan) === true &&
    HelptapEditor.dragAndDrop.canReceiverAccept(editor, selection, findDestination(drop.plan), 'highlightDropZone') === true;

  return drop;

}
